import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import {
  CompanyCostItem,
  CostItem,
  OpsDocument,
  ModalService,
  CostItemService,
  CompanyCostItemService,
  ProjectStatusGuardService,
} from '@ops/shared-types';
import { Subscription, take } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { Miscellanious } from '@ops/global-config';

interface FileMap extends OpsDocument {
  originalUrl: string;
  extensionName: string;
  fileName: string;
}

@Component({
  selector: 'ops-document-modal',
  templateUrl: './document-modal.component.html',
  styleUrls: ['./document-modal.component.scss'],
})
export class DocumentModalComponent implements OnInit, OnDestroy {
  @Input() id: string;
  @Input() type: 'company' | 'project';
  @Input() item: CompanyCostItem | CostItem;
  @Input() title: string; 
  @Output() filesUploaded: EventEmitter<CompanyCostItem | CostItem> =
    new EventEmitter();

  readonly: boolean = false;
  uploading: boolean = false;

  private element: HTMLElement;
  private dataService: CostItemService | CompanyCostItemService;

  invoiceFiles: Array<OpsDocument> = [];
  paymentFiles: Array<OpsDocument> = [];
  
  invoiceFilesMap: Array<FileMap> = [];
  paymentFilesMap: Array<FileMap> = [];

  subscriptions: Array<Subscription> = [];

  preventDelete: boolean = false;
  removing: boolean;

  constructor(
    private el: ElementRef,
    private modalService: ModalService,
    private costItemService: CostItemService,
    private companyItemService: CompanyCostItemService,
    private changeDetector: ChangeDetectorRef,
    private translateService: TranslateService,
    private projectStatusGuard: ProjectStatusGuardService
  ) {
    this.element = this.el.nativeElement;
    this.projectStatusGuard.getProjectStatusPrevention$().pipe(take(1)).subscribe((prevention) => {
      this.preventDelete = !!prevention;
    });
  }

  ngOnInit() {
    this.element.style.display = 'none';
    if (!this.id) {
      console.error('@Input id is mandatory');
      return;
    }

    document.body.appendChild(this.element);
    this.modalService.add(this);
    // console.log(this.type);
    this.dataService =
      this.type === 'company' ? this.companyItemService : this.costItemService;

    this.setTitle();
    
    // Listen for language changes to update title
    const langChangeSub = this.translateService.onLangChange.subscribe(() => {
      this.setTitle();
    });
    this.subscriptions.push(langChangeSub);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.element.remove();
  }

  open(params?: any): void {
    this.readonly = params?.readonly ?? false;
    this.element.style.display = 'flex';
    this.element.focus();

    this.invoiceFiles = 
      this.item.documents?.filter((d) => {return d.type?.toUpperCase() === 'INVOICE'}) ??
      [];
    this.paymentFiles =
      this.item.documents?.filter((d) => d.type?.toUpperCase() === 'PAYMENT') ??
      [];
    
    this.updateFileMaps();
  }

  close(event?: any): void {
    event?.stopPropagation();
    this.element.style.display = 'none';
  }

  uploadFiles(event: any, type: 'invoice' | 'payment') {
    var files: Array<File> = event.target.files as Array<File>;
    files = [...(files as Array<File>)];

    files.forEach((f, index, array) => {
      if (f.size > 1024 * 1024 * 15) {
        window.alert(this.translateService.instant('windowAlert.documentModal.fileTooBig', { fileName: f.name }));
        array.splice(index, 1);
        event.target.value = '';
        return;
      }
      if(this.item.documents?.find((d) => (d.name === f.name) && (d.type === type))) {
        window.alert(this.translateService.instant('windowAlert.documentModal.fileAlreadyUploaded', { fileName: f.name }));
        array.splice(index, 1);
        event.target.value = '';
        return;
      }
    });
    // console.log('TYPE : ', this.type);
    if (files.length === 0) return;

    this.uploading = true;
    // this.loadingService.startLoadingSpinner();

    const sub = this.dataService.attachFiles(this.item, type, files).subscribe({
      next: (responseItem) => {
        this.invoiceFiles = responseItem.documents.filter(
          (d: any) => d.type?.toUpperCase() === 'INVOICE'
        );
        this.paymentFiles = responseItem.documents.filter(
          (d: any) => d.type?.toUpperCase() === 'PAYMENT'
        );
        this.updateFileMaps();
        this.changeDetector.detectChanges();
        // this.loadingService.stopLoadingSpinner();
        this.filesUploaded.emit(responseItem);
        this.item = responseItem;

        this.changeDetector.markForCheck();

        this.uploading = false;
        event.target.value = '';
      },
      error: (error) => {
        window.alert(this.translateService.instant('windowAlert.generic.error', { error: error.error.message }));
        this.uploading = false;
        event.target.value = '';
      },
    });
    this.subscriptions.push(sub);
  }

  downloadFile(file: OpsDocument) {
    // this.loadingService.startLoadingSpinner();
    // console.log(file.path);
    const sub = this.dataService
      .downloadFile(this.item, file._id ?? '')
      .subscribe({
        next: (blob: Blob) => {
          let objectURL = URL.createObjectURL(blob);
          // const newWindow = window.open(objectURL, '_blank');

          // if (!newWindow) {
          //   console.error('Popup blocked. Unable to open PDF.');
          // }
          const downloadLink = document.createElement('a');
          downloadLink.target = '_self';
          downloadLink.href = objectURL;
          downloadLink.download = file.name ?? '';
          document.body.appendChild(downloadLink);
          downloadLink.click();
        },
        error: (error) => {
          window.alert(this.translateService.instant('windowAlert.generic.error', { error: error.error.message }));
        },
      });
    this.subscriptions.push(sub);
  }

  previewFile(file: OpsDocument) {
    // if (this.readonly) return;
    const sub = this.dataService
      .previewFile(this.item, file._id ?? '')
      .subscribe({
        next: (url: any) => {
          const newWindow = window.open(url, '_blank');

          if (!newWindow) {
            console.error('Popup blocked. Unable to open PDF.');
          }
        },
        error: (error) => {
          window.alert(this.translateService.instant('windowAlert.generic.error', { error: error.error.message }));
        },
      });

    this.subscriptions.push(sub);
  }

  removeFile(file: OpsDocument) {
    // this.loadingService.startLoadingSpinner();
    this.removing = true;
    const sub = this.dataService
      .removeFile(this.item, file._id ?? '')
      .subscribe({
        next: (responseItem: CompanyCostItem | CostItem) => {
          
          this.invoiceFiles =
            responseItem.documents?.filter(
              (d) => d.type?.toUpperCase() === 'INVOICE'
            ) ?? [];
          this.paymentFiles =
            responseItem.documents?.filter(
              (d) => d.type?.toUpperCase() === 'PAYMENT'
            ) ?? [];
          this.updateFileMaps();
          this.item = responseItem;
          this.changeDetector.detectChanges();

          window.alert(this.translateService.instant('windowAlert.documentModal.fileRemoved', { fileName: file.name?.slice(26) }));
          this.filesUploaded.emit(responseItem);
          this.removing = false;
        },
        error: (error) => {
          window.alert(this.translateService.instant('windowAlert.generic.error', { error: error.error.message }));
          this.removing = false;
        },
      });
    this.subscriptions.push(sub);
  }



  addWordBreaksToFileName(filename: string) {
    return Miscellanious.makeStringCustomBreak(filename, filename.length - 4);
  }

  private setTitle() {
    if (this.type === 'company') {
      const accountingCategory = this.translateService.instant(this.item.accountingCategory) || this.item.accountingCategory;
      const subcategory = this.translateService.instant((this.item as CompanyCostItem).subcategory) || (this.item as CompanyCostItem).subcategory;
      const name = this.item.name;
      
      this.title = `${accountingCategory} ${subcategory} ${name}`;
      // console.log('Company title set:', this.title);
      return;
    }

    const position = this.translateService.instant((this.item as CostItem).position) || (this.item as CostItem).position;
    const name = this.item.name;
    
    this.title = `${this.title} ${position} ${name}`;
    // console.log('Project title set:', this.title);
  }

  private createFileMap(file: OpsDocument): FileMap {
    const fullFileName = file.name || '';
    const extensionName = fullFileName.split('.').pop() || '';
    const fileName = fullFileName.replace(`.${extensionName}`, '') || fullFileName;
    const originalUrl = file.path || '';
    
    return {
      ...file,
      originalUrl,
      extensionName,
      fileName
    };
  }

  private updateFileMaps(): void {
    this.invoiceFilesMap = this.invoiceFiles.map(file => this.createFileMap(file));
    this.paymentFilesMap = this.paymentFiles.map(file => this.createFileMap(file));
  }
}
